// src/components/Forms/AddVehicleForm.js

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import axiosClient from '../../api/axiosClient';
import { toast } from 'react-toastify';
import Button from '../Button/Button';
import InputText from '../Input/InputText';
import InputImage from '../Input/InputImage';
import Select from '../Select/Select';
import Typography from '../Typography';
import { useVehicles } from '../../context/VehicleContext';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import { getVehicleTypesWithLabels, useVehicleTypeTranslations } from '../../constants/vehicleTypes';

const AddVehicleForm = ({ onSubmit, vehicle, onClose }) => {
  const { t } = useTranslation('userProfile');
  const { getVehicleTypeLabel } = useVehicleTypeTranslations();

  const [formData, setFormData] = useState({
    vehicleType: 'motorcycle',
    brand: '',
    customBrand: '',
    model: '',
    customModel: '',
    nickname: '',
    year: '',
    image: null,
  });

  // Crear opciones usando el hook de traducciones
  const vehicleOptions = getVehicleTypesWithLabels().map(option => ({
    ...option,
    label: getVehicleTypeLabel(option.value)
  }));
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [fileWarning, setFileWarning] = useState('');
  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const fileInputRef = useRef();

  const { loading } = useVehicles();

  // Cargar marcas al montar y cuando cambie el tipo de vehículo
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axiosClient.get(`/api/vehicles/brands?vehicleType=${formData.vehicleType}`);

        const loadedBrands = response.data.brands || [];
        setBrands(loadedBrands);
        // Solo resetear marca y modelo si NO estamos editando un vehículo
        if (!vehicle) {
          setFormData(prev => ({
            ...prev,
            brand: '',
            customBrand: '',
            model: '',
            customModel: ''
          }));
          setModels([]);
          const shouldUseCustomFields = loadedBrands.length === 0;
          setIsCustomBrand(shouldUseCustomFields);
          setIsCustomModel(shouldUseCustomFields);
        }
      } catch (error) {
        console.error('❌ Error loading brands', error);
        setBrands([]);
        setModels([]);
        setIsCustomBrand(true);
        setIsCustomModel(true);
        if (!vehicle) {
          setFormData(prev => ({
            ...prev,
            brand: '',
            model: ''
          }));
        }
        toast.error(t('garage.vehicle.errorLoadingBrands'));
      }
    };
    fetchBrands();
  }, [formData.vehicleType, t, vehicle]);

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      vehicleType: 'motorcycle',
      brand: '',
      customBrand: '',
      model: '',
      customModel: '',
      nickname: '',
      year: '',
      image: null,
    });
    setFile(null);
    setIsCustomBrand(false);
    setIsCustomModel(false);
    setModels([]);
    setErrors({});
  };

  // Efecto para manejar la edición de vehículos
  useEffect(() => {
    if (vehicle) {
      setFormData({
        vehicleType: vehicle.vehicleType || 'motorcycle',
        brand: vehicle.brand || '',
        customBrand: '',
        model: vehicle.model || '',
        customModel: '',
        nickname: vehicle.nickname || '',
        year: vehicle.year || '',
        image: {
          file: null,
          previewUrl: vehicle.image,
        },
      });
      setFile(null);
    }
  }, [vehicle]);

  // Efecto separado para verificar marca personalizada cuando brands se carga
  useEffect(() => {
    if (vehicle && brands.length > 0) {
      const checkCustomBrand = async () => {
        if (!brands.includes(vehicle.brand)) {
          // Marca personalizada
          setIsCustomBrand(true);
          setIsCustomModel(true); // Asegurar que isCustomModel esté activo
          setFormData((prev) => ({
            ...prev,
            customBrand: vehicle.brand,
            brand: '',
            customModel: vehicle.model,
            model: '',
          }));
          setModels([]);
        } else {
          // Marca existente, cargar modelos
          setIsCustomBrand(false);
          setIsCustomModel(false); // Inicializar isCustomModel a false
          const loadedModels = await loadModels(vehicle.brand, vehicle.vehicleType);
          // console.log('Modelos cargados:', loadedModels);
          // console.log('Modelo del vehículo:', vehicle.model);
          if (loadedModels.includes(vehicle.model)) {
            // Modelo existente
            setIsCustomModel(false);
            setFormData((prev) => ({
              ...prev,
              model: vehicle.model,
              customModel: '',
            }));
          } else {
            // Modelo personalizado
            setIsCustomModel(true);
            setFormData((prev) => ({
              ...prev,
              model: '',
              customModel: vehicle.model,
            }));
          }
        }
      };

      checkCustomBrand();
    }
  }, [vehicle, brands, formData.vehicleType]);

  // Función para cargar modelos según la marca y tipo de vehículo
  const loadModels = async (brand, vehicleType = formData.vehicleType) => {
    try {
      const response = await axiosClient.get(`/api/vehicles/models?brand=${brand}&vehicleType=${vehicleType}`);
      setModels(response.data.models);
      return response.data.models; // Retornar los modelos cargados
    } catch (error) {
      toast.error(t('garage.vehicle.errorLoadingModels'));
      return [];
    }
  };

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleBrandChange = (e) => {
    const value = e.target.value;

    if (value === 'Other Brand') {
      setIsCustomBrand(true);
      setIsCustomModel(true);
      setFormData((prevFormData) => ({
        ...prevFormData,
        brand: '',
        customBrand: '',
        model: '',
        customModel: '',
      }));
      setModels([]);
    } else {
      setIsCustomBrand(false);
      setFormData((prevFormData) => ({
        ...prevFormData,
        brand: value,
        customBrand: '',
        model: '',
        customModel: '',
      }));
      setIsCustomModel(false);
      loadModels(value);
    }
  };

  const handleCustomBrandChange = (e) => {
    const value = e.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      customBrand: value,
      model: '',
      customModel: '',
    }));
    if (brands.includes(value)) {
      setIsCustomBrand(false);
      loadModels(value);
    }
  };

  const handleModelChange = (e) => {
    const value = e.target.value;

    if (value === 'Other Model') {
      setIsCustomModel(true);
      setFormData((prevFormData) => ({
        ...prevFormData,
        model: '',
        customModel: '',
      }));
    } else {
      setIsCustomModel(false);
      setFormData((prevFormData) => ({
        ...prevFormData,
        model: value,
        customModel: '',
      }));
    }
  };

  const handleCustomModelChange = (e) => {
    const value = e.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      customModel: value,
    }));
    if (models.includes(value)) {
      setIsCustomModel(false);
      setFormData((prevFormData) => ({
        ...prevFormData,
        model: value,
        customModel: '',
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validar tipo de archivo
    if (!selectedFile.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, file: 'Solo se permiten imágenes.' }));
      return;
    }

    // Avisar si el archivo es muy grande (mayor a 2 MB)
    if (selectedFile.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, file: 'La imagen seleccionada es demasiado grande.' }));
      return;
    }

    // Validar dimensiones y relación de aspecto 4:3
    const img = new Image();
    img.src = URL.createObjectURL(selectedFile);
    img.onload = () => {
      const ratio = img.width / img.height;
      if (Math.abs(ratio - (4 / 3)) > 0.01) {
        setFileWarning(t('garage.vehicle.imageWarning'));
      } else {
        setFileWarning('');
      }

      const imagePreviewUrl = URL.createObjectURL(selectedFile);
      setFormData(prevFormData => ({
        ...prevFormData,
        image: { file: selectedFile, previewUrl: imagePreviewUrl },
      }));
      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: '' }));
    };
  };

  // Validar el formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar Marca
    if (isCustomBrand) {
      if (!formData.customBrand.trim()) {
        // newErrors.customBrand = 'Debes especificar la marca personalizada.';
        newErrors.customBrand = t('garage.vehicle.errorCustomBrand');
      }
    } else {
      if (!formData.brand) {
        // newErrors.brand = 'La marca es obligatoria.';
        newErrors.brand = t('garage.vehicle.errorBrand');

      }
    }

    // Validar Modelo
    if (isCustomModel || isCustomBrand) {
      if (!formData.customModel.trim()) {
        // newErrors.customModel = 'Debes especificar el modelo personalizado.';
        newErrors.customModel = t('garage.vehicle.errorCustomModel');

      }
    } else {
      if (!formData.model) {
        // newErrors.model = 'El modelo es obligatorio.';
        newErrors.model = t('garage.vehicle.errorModel');
      }
    }

    // Validar Año
    if (!formData.year) {
      // newErrors.year = 'El año es obligatorio.';
      newErrors.year = t('garage.vehicle.errorYear');

    } else if (isNaN(formData.year) || formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      // newErrors.year = 'El año es inválido.';
      newErrors.year = t('garage.vehicle.errorYearInvalid');
    }

    // Validar Imagen - Ya no es requerida
    // if (!formData.image?.file && !vehicle?.image) {
    //   //newErrors.file = 'La imagen es obligatoria.';
    //   newErrors.file = t('garage.vehicle.errorImage');
    // }

    return newErrors;
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // toast.error(t('garage.vehicle.errorForm'));
      // toast.error('Por favor, corrige los errores en el formulario.');
      return;
    }

    setErrors({});

    try {
      const vehicleData = new FormData();
      vehicleData.append('vehicleType', formData.vehicleType);
      vehicleData.append('brand', isCustomBrand ? formData.customBrand : formData.brand);
      vehicleData.append('model', isCustomModel ? formData.customModel : formData.model);
      vehicleData.append('nickname', formData.nickname);
      vehicleData.append('year', formData.year);

      if (formData.image?.file) {
        vehicleData.append('image', formData.image.file);
      }

      await onSubmit(vehicleData);
    } catch (error) {
      console.error(t('garage.vehicle.errorSavingVehicle'), error);
      // toast.error('Error al guardar el vehículo.');
      toast.error(t('garage.vehicle.errorSavingVehicle'));
    }
  };

  return (
    <FormContainer>
      {/* <form onSubmit={handleSubmit}> */}
      <FormItem>
        <InputImage
          imgSrc={formData.image?.previewUrl || (vehicle ? vehicle.image : '')}
          onChangeFile={handleFileChange}
          fileInputRef={fileInputRef}
          inputFileId="vehicleImage"
          errors={errors}
          warning={fileWarning}
          required={false}
          vehicleType={formData.vehicleType}
        />
      </FormItem>

      <FormItem>
        <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultWeak}>
          {t('garage.vehicle.vehicleType')}
        </Typography>
        <Select
          name="vehicleType"
          value={formData.vehicleType}
          onChange={(e) => {
            setFormData(prev => ({
              ...prev,
              vehicleType: e.target.value
            }));
          }}
          required
        >
          <option value="" disabled>Selecciona un tipo de vehículo</option>
          {vehicleOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormItem>

      <FormItem>
        <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultWeak}>
          {t('garage.vehicle.brand')}
        </Typography>
        <RowWrapper>
          {!isCustomBrand && (
            <Select
              name="brand"
              onChange={handleBrandChange}
              value={formData.brand}
              required
              style={{ minHeight: "38px" }}
            >
              <option value="">
                {t('garage.vehicle.emptyStateBrand')}
              </option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
              <option value="Other Brand">
                {t('garage.vehicle.customBrand')}
              </option>
            </Select>
          )}
          {isCustomBrand && (
            <InputText
              name="customBrand"
              value={formData.customBrand}
              onChange={handleCustomBrandChange}
              placeholder={t('garage.vehicle.enterCustomBrand')}
              required
            />
          )}
        </RowWrapper>
        {errors.brand && <ErrorText>{errors.brand}</ErrorText>}
        {errors.customBrand && <ErrorText>{errors.customBrand}</ErrorText>}
      </FormItem>

      <FormItem>
        <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultWeak}>
          {t('garage.vehicle.model')}
        </Typography>
        <RowWrapper>
          {!isCustomBrand && !isCustomModel && (
            <Select
              name="model"
              onChange={handleModelChange}
              value={formData.model}
              required
              style={{ minHeight: "38px" }}
            >
              <option value="">
                {t('garage.vehicle.emptyStateModel')}
              </option>
              {models.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
              <option value="Other Model">
                {t('garage.vehicle.customModel')}
              </option>
            </Select>
          )}
          {(isCustomBrand || isCustomModel) && (
            <InputText
              name="customModel"
              value={formData.customModel}
              onChange={handleCustomModelChange}
              placeholder={t('garage.vehicle.enterCustomModel')}
              required
            />
          )}
        </RowWrapper>
        {errors.model && <ErrorText>{errors.model}</ErrorText>}
        {errors.customModel && <ErrorText>{errors.customModel}</ErrorText>}
      </FormItem>

      <FormItem>
        <RowWrapper>
          <FormGroup>
            <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultWeak}>
              {t('garage.vehicle.year')}
            </Typography>
            <InputText
              type="number"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              required
            />
            {errors.year && <ErrorText>{errors.year}</ErrorText>}
          </FormGroup>
          <FormGroup>
            <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultWeak}>
              {t('garage.vehicle.nickname')}
            </Typography>
            <InputText
              type="text"
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
            />
          </FormGroup>
        </RowWrapper>
      </FormItem>

      <ActionsContainer>
        <Button
          $variant="outline"
          size="l"
          $contentAlign="center"
          onClick={onClose}
        >
          {t('garage.vehicle.cancel')}
        </Button>
        {/* <Button type="submit" disabled={loading}>
            {loading ? t('garage.vehicle.saving') : (vehicle ? t('garage.vehicle.saveVehicleChanges') : t('garage.vehicle.saveVehicle'))}
          </Button> */}
        <Button
          type="button"
          size="l"
          disabled={loading}
          onClick={(e) => {
            handleSubmit(e); // llamamos manualmente a handleSubmit
          }}
        >
          {loading
            ? t('garage.vehicle.saving')
            : vehicle
              ? t('garage.vehicle.saveVehicleChanges')
              : t('garage.vehicle.saveVehicle')
          }
        </Button>
      </ActionsContainer>
      {/* </form> */}
    </FormContainer>
  );
};

AddVehicleForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  vehicle: PropTypes.object, // Puede ser null o undefined si se está creando un nuevo vehículo
  onClose: PropTypes.func, // Opcional: si es necesario
};

export default AddVehicleForm;

// Styled Components

const FormContainer = styled.div`
  padding-top: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const FormItem = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  margin-bottom: 16px;
  padding-left: ${({ theme }) => theme.sizing.sm};
  padding-right: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const RowWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const ErrorText = styled.p`
  color: red;
  font-size: 0.8rem;
`;

const ActionsContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;
